import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateAgent } from "@/features/kyc/api/use-create-agent";
import type { AgentDto } from "@/features/kyc/api/dto";
import {
  createAgentSchema,
  type AgentFormValues,
} from "@/features/kyc/schemas/agent.schema";
import { AGENT_TYPES } from "@/features/shared/api/enums";

const EMPTY_VALUES: AgentFormValues = {
  keycloakUserId: "",
  fullName: "",
  agencyCode: "",
  zone: "",
  agentType: "BOTH",
  floatWalletId: "",
};

/** Chaînes vides → undefined : les champs omis prennent le défaut serveur. */
const toPayload = (values: AgentFormValues): AgentDto =>
  Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, v === "" ? undefined : v])
  ) as unknown as AgentDto;

/**
 * Formulaire d'enrôlement d'un agent (`POST /Agents/add`) — TanStack Form +
 * schéma zod mémoïsé sur la langue courante (règle projet : jamais
 * d'instance figée).
 */
export function AgentFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createAgentSchema(), [i18n.language]);

  const createAgent = useCreateAgent();
  const pending = createAgent.isPending;
  const [preview, setPreview] = useState<AgentDto | null>(null);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs vides.
  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const fieldError = (name: keyof AgentFormValues) => (props: { value: unknown }) => {
    const shape = schema.shape[name];
    const result = shape.safeParse(props.value ?? "");
    return result.success ? undefined : result.error.issues[0]?.message;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("kyc.agents.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          {(
            [
              ["keycloakUserId", "text"],
              ["fullName", "text"],
              ["agencyCode", "text"],
              ["zone", "text"],
              ["floatWalletId", "text"],
            ] as const
          ).map(([name, type]) => (
            <form.Field key={name} name={name} validators={{ onChange: fieldError(name) }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor={name}>{t(`kyc.agents.form.${name}`)}</Label>
                  <Input
                    id={name}
                    type={type}
                    value={(field.state.value as string) ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value as never)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          ))}

          <form.Field name="agentType" validators={{ onChange: fieldError("agentType") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{t("kyc.agents.form.agentType")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as AgentFormValues["agentType"])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={(key) => t(key as never)}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        createAgent.mutate(preview, {
          onSuccess: () => {
            notifSilence(t("kyc.agents.feedback.created"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
